import PropTypes from 'prop-types'
import {useCallback} from 'react'
import {Switch, FormControl, FormLabel, useDisclosure} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import useSWR from 'swr'

import {CrossIcon} from '../../../icons'
import {ToastMessage} from '../../../components'
import ExcludePayerModal from './ExcludePayerModal'
import {fetcher, patchHeaders} from '../../../../api'
import {camelizeResult} from '../../../../api/serializer'
import {toLocalDate} from '../../../../utils/formatter'

import {
  SubsectionTitle,
  SubsectionContainer,
  SubsectionDescription,
  SubsectionAlert,
} from '../../../components/Subsection'

const onError = (message) => {
  toast.dark(
    <ToastMessage>
      <CrossIcon mr={1} color="mld.alert.700" />
      {message}
    </ToastMessage>,
  )
}

const SendStatus = ({
  supplierId,
  customerId,
  customerName,
  nextSendOn,
  authenticityToken,
}) => {
  const {t} = useTranslation('customers')

  const {isOpen, onClose, onOpen} = useDisclosure()

  const url = `/api/suppliers/${supplierId}/customers/${customerId}/payer_statement_exclusions`

  const {data, mutate, isLoading} = useSWR(url, fetcher, {
    use: [camelizeResult],
  })

  const changeState = useCallback(
    async ({next, verb}) => {
      const result = await fetcher(
        `${url}/${verb}`,
        patchHeaders({authenticityToken, raiseClientErrors: false}),
      )

      if (result.error) {
        onError(result.error)
        return
      }

      mutate({...data, status: next})
    },
    [authenticityToken, url, mutate, data],
  )

  const onChange = useCallback(() => {
    if (!data) {
      return
    }

    if (data.status === 'enabled') {
      onOpen()
      return
    }

    changeState({next: 'enabled', verb: 'enable'})
  }, [data, changeState, onOpen])

  const nextStatementDate = toLocalDate(nextSendOn, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  const isChecked = !isLoading && data.status === 'enabled'

  const disableIneligibilityReason = data?.disableIneligibilityReasons[0]

  const isDisabled =
    isLoading || (data.status === 'enabled' && disableIneligibilityReason)

  const label =
    isLoading || data.status === 'enabled'
      ? `${t('customers:Statements.SendStatus.switch.on')} ${nextStatementDate}`
      : t('customers:Statements.SendStatus.switch.off')

  const warning =
    disableIneligibilityReason && data?.status === 'enabled'
      ? t(
          `customers:Statements.SendStatus.ineligbilityReasons.${disableIneligibilityReason}`,
        )
      : null

  const excludeSuccess = data?.status === 'disabled'

  return (
    <SubsectionContainer data-testid="payer-statement-send-status" gap="xs">
      <SubsectionTitle title={t('customers:Statements.SendStatus.title')}>
        <FormControl
          display="flex"
          alignItems="center"
          width="auto"
          gap={1}
          flexWrap="wrap"
        >
          <FormLabel
            htmlFor="email-alerts"
            textStyle="body-detail"
            mb={0}
            color="mld.neutral.700"
          >
            {label}
          </FormLabel>
          <Switch
            variant="basic"
            isChecked={isChecked}
            isDisabled={isDisabled}
            onChange={onChange}
            id="exclude-from-statement"
            data-testid="exclude-from-statement"
          />
        </FormControl>
      </SubsectionTitle>
      <SubsectionDescription
        description={t('customers:Statements.SendStatus.description')}
      />
      <SubsectionAlert text={warning} />
      <ExcludePayerModal
        exclude={() => changeState({next: 'disabled', verb: 'disable'})}
        success={excludeSuccess}
        isOpen={isOpen}
        onClose={onClose}
        customerName={customerName}
      />
    </SubsectionContainer>
  )
}

SendStatus.propTypes = {
  supplierId: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  customerName: PropTypes.string.isRequired,
  nextSendOn: PropTypes.string.isRequired,
  authenticityToken: PropTypes.string.isRequired,
}

export default SendStatus
